//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { GridCol, GridRow } from 'dibs-elements/exports/Grid';
import { OverviewAbout } from './Overview/OverviewAbout';
import { OverviewAwards } from './Overview/OverviewAwards';
import { OverviewArticles } from './Overview/OverviewArticles';
import { OverviewWeblinks } from './Overview/OverviewWeblinks';
import OverviewInstagram from './Overview/OverviewInstagram';
import { OverviewFavorites } from './Overview/OverviewFavorites';
import { OverviewProjects } from './Overview/OverviewProjects';

import styles from './OverviewRoot.scss';

import type { OverviewRoot_viewer as Viewer } from './__generated__/OverviewRoot_viewer.graphql';
import type { PageType } from './constants';

type Props = {
    adminHost: string,
    viewer: ?Viewer,
    isClient: boolean,
    isAdminMode: boolean,
    isEditMode: boolean,
    onChangePage: (pageType: PageType) => void,
    isTrade: boolean,
};

export const OverviewRootComponent = ({
    viewer,
    isClient,
    isAdminMode,
    isEditMode,
    onChangePage,
    adminHost,
    isTrade,
}: Props) => {
    const tradeFirm = viewer?.tradeFirms?.[0];

    const hasInstagram: boolean = !!tradeFirm?.instagram && viewer?.tradePortfolioInstagram;
    const hasArticles: boolean = !!(tradeFirm?.publicProfile?.articles || []).length;

    return (
        <div className={styles.wrapper}>
            <div className={styles.rowWrapper}>
                <GridRow>
                    <OverviewAbout tradeFirm={(tradeFirm: ?Object)} isEditMode={isEditMode} />
                    <GridCol
                        desktopOptions={{
                            offset: 1,
                        }}
                        tabletLandscape={3}
                        mobile={12}
                    >
                        <OverviewAwards
                            tradeFirm={(tradeFirm: ?Object)}
                            isEditMode={isEditMode}
                            viewer={(viewer || null: ?Object)}
                        />
                        <OverviewWeblinks
                            tradeFirm={(tradeFirm: ?Object)}
                            isEditMode={isEditMode}
                        />
                    </GridCol>
                </GridRow>
            </div>
            <div className={styles.projectsWrapper}>
                <OverviewProjects
                    isClient={isClient}
                    adminHost={adminHost}
                    tradeFirm={(tradeFirm: ?Object)}
                    isAdminMode={isAdminMode}
                    isEditMode={isEditMode}
                    onChangePage={onChangePage}
                />
            </div>
            <OverviewFavorites
                tradeFirm={(tradeFirm: ?Object)}
                isEditMode={isEditMode}
                isTrade={isTrade}
                onChangePage={onChangePage}
            />
            {hasInstagram || hasArticles || isEditMode ? (
                <div className={styles.alternateRowWrapper}>
                    <div className={`${styles.rowWrapper} rowFlex`}>
                        <OverviewArticles
                            isEditMode={isEditMode}
                            tradeFirm={(tradeFirm: ?Object)}
                            hasInstagram={hasInstagram}
                        />
                        <OverviewInstagram
                            tradeFirm={(tradeFirm: ?Object)}
                            viewer={viewer}
                            isEditMode={isEditMode}
                            hasArticles={hasArticles}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export const OverviewRoot = createFragmentContainer(OverviewRootComponent, {
    viewer: graphql`
        fragment OverviewRoot_viewer on Viewer {
            ...OverviewInstagram_viewer
            tradePortfolioInstagram: featureFlag(feature: "tradePortfolioInstagram")
            tradeFirms(urlLabel: $firmUrlLabel) @include(if: $shouldRenderOverview) {
                ...OverviewAbout_tradeFirm
                ...OverviewAwards_tradeFirm
                ...OverviewArticles_tradeFirm
                ...OverviewWeblinks_tradeFirm
                ...OverviewInstagram_tradeFirm
                ...OverviewFavorites_tradeFirm
                ...OverviewProjects_tradeFirm
                publicProfile {
                    articles {
                        webLink {
                            url
                        }
                    }
                }
                instagram {
                    __id
                }
            }
            ...OverviewAwards_viewer
        }
    `,
});
